import {
    Box,
    Image as CaspecoImage,
    Flex,
    Grid,
    GridItem,
    Skeleton,
    ThemeColorVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import Header from "layout/header";
import { LayoutContext } from "layout/layout-context";
import { useEffect, useRef, useState } from "react";
import { CSSVarHeaderHeight, CONTENT_MAX_WIDTH } from "style-variables";
import { AppMode, getAppMode, getDocumentModeClasses } from "types/app-mode";
import "./layout.css";
// eslint-disable-next-line caspeco-react/discourage-chakra-import
import { AspectRatio, useBreakpointValue } from "@chakra-ui/react";
import { useLocation, Outlet } from "react-router-dom";
import { checkIsMyBookingPage, checkIsExperienceViewPage } from "routes";

const isIframe = window.self !== window.top;
let observer: ResizeObserver;

function Layout({ children }: { children?: JSX.Element }) {
    const appMode = getAppMode();
    const location = useLocation();
    const isExperienceViewPage = checkIsExperienceViewPage(location.pathname);
    const [showCoverImage, setShowCoverImage] = useState(!isIframe && !isExperienceViewPage);
    const { data: settings, isPending } = useExternalBookingSettings();

    const isMyBookingPage = checkIsMyBookingPage(location.pathname);
    const [isCoverImageLoaded, setIsCoverImageLoaded] = useState(false);
    const [loadedMyBookingBackgroundImageUrl, setLoadedMyBookingBackgroundImageUrl] = useState<
        string | undefined
    >();
    const [readyMyBookingBackgroundImageUrl, setReadyMyBookingBackgroundImageUrl] = useState<
        string | undefined
    >();
    const [hasAsideContent, setHasAsideContent] = useState(false);
    const asideRef = useRef<HTMLDivElement>(null);

    const aspectRatio = useBreakpointValue({
        base: 1 / 1,
        md: 32 / 9,
    });

    const themeSettings = settings?.themeSettings;
    const coverImageUrl =
        aspectRatio === 1
            ? themeSettings?.coverImageOneByOne.imageUrl
            : themeSettings?.coverImageThirtyTwoByNine.imageUrl;
    const hasCoverImage = Boolean(
        themeSettings?.coverImageOneByOne.imageUrl &&
        themeSettings?.coverImageThirtyTwoByNine.imageUrl
    );
    const finalizedImageUrl = themeSettings?.finalizationImage?.imageUrl;
    const hasMyBookingBackgroundImage = Boolean(finalizedImageUrl) && isMyBookingPage;
    const isMyBookingBackgroundImageLoaded =
        loadedMyBookingBackgroundImageUrl === finalizedImageUrl;
    const isMyBookingBackgroundImageReady =
        !hasMyBookingBackgroundImage || readyMyBookingBackgroundImageUrl === finalizedImageUrl;
    const shouldShowMyBookingBackgroundImage =
        isMyBookingBackgroundImageLoaded && hasMyBookingBackgroundImage;

    const container = useRef(null);
    if (isIframe && container.current && !observer) {
        observer = new ResizeObserver((entries) => {
            const { height } = entries[0].contentRect;
            window.parent.postMessage(`caspeco-booking-height ${height}`, "*");
        });
        observer.observe(container.current);
    }

    useEffect(() => {
        const classes = getDocumentModeClasses(appMode);
        if (!classes.length) return;

        const parentElement = document.body.parentNode as Element | null;
        if (parentElement && parentElement.classList) {
            parentElement.classList.add(...classes);
        }
    }, [appMode]);

    useEffect(() => {
        if (!coverImageUrl) {
            return;
        }
        const handleCoverImageLoad = () => {
            setIsCoverImageLoaded(true);
        };
        const coverImage = new Image();
        coverImage.src = coverImageUrl;
        coverImage.onload = handleCoverImageLoad;

        return () => {
            coverImage.onload = null;
        };
    }, [coverImageUrl]);

    useEffect(() => {
        if (!finalizedImageUrl) {
            return;
        }

        const handleFinalizedImageLoad = () => {
            setLoadedMyBookingBackgroundImageUrl(finalizedImageUrl);
            setReadyMyBookingBackgroundImageUrl(finalizedImageUrl);
        };
        const handleFinalizedImageError = () => {
            setReadyMyBookingBackgroundImageUrl(finalizedImageUrl);
        };
        const finalizedImage = new Image();
        finalizedImage.src = finalizedImageUrl;
        finalizedImage.onload = handleFinalizedImageLoad;
        finalizedImage.onerror = handleFinalizedImageError;

        return () => {
            finalizedImage.onload = null;
            finalizedImage.onerror = null;
        };
    }, [finalizedImageUrl]);
    useEffect(() => {
        const checkAsideContent = () => {
            if (asideRef.current) {
                const hasElements = asideRef.current.children.length > 0;

                setHasAsideContent(hasElements);
            }
        };
        checkAsideContent();

        const mutationObserver = new MutationObserver(checkAsideContent);
        if (asideRef.current) {
            mutationObserver.observe(asideRef.current, {
                childList: true, // Watch for changes in child elements
            });
        }

        // Cleanup observer on component unmount
        return () => {
            mutationObserver.disconnect();
        };
    }, []);

    // Define the specific column width for the aside, taken from Figma
    const asideWidth = "22.5rem";

    const notEmbedded = !isIframe || appMode === AppMode.Modal;

    const renderCoverImageSection = () => {
        // Show header placeholder while loading external booking settings
        if (isPending) {
            return <Box minH={CSSVarHeaderHeight} w="full" />;
        }

        // Only show cover image if enabled and available
        if (!showCoverImage || !hasCoverImage) {
            return null;
        }

        return (
            <Box minH={CSSVarHeaderHeight} w="full">
                <AspectRatio
                    position="relative"
                    ratio={aspectRatio}
                    width="full"
                    height="full"
                    overflow="hidden"
                >
                    {isCoverImageLoaded ? (
                        <CaspecoImage
                            src={coverImageUrl}
                            alt="Cover image"
                            objectFit="cover"
                            width="full"
                            height="full"
                        />
                    ) : (
                        <Skeleton
                            startColor={ThemeColorVariable.NeutralContainer}
                            endColor={ThemeColorVariable.NeutralContainer}
                            width="full"
                            height="full"
                        />
                    )}
                </AspectRatio>
            </Box>
        );
    };

    /* Background overlay opacity to increase contrast and readability */
    const BG_OVERLAY_OPACITY = "rgba(0, 0, 0, 0.48)";
    return (
        <Box
            ref={container}
            width="100%"
            minH="100%"
            color={
                shouldShowMyBookingBackgroundImage
                    ? ThemeColorVariable.Background // We invert the colors for now since we do not have a good dark theme solution
                    : ThemeColorVariable.OnBackground
            }
            position="relative"
        >
            {shouldShowMyBookingBackgroundImage ? (
                <>
                    <Box
                        position="fixed"
                        top={0}
                        left={0}
                        w="100vw"
                        h="100vh"
                        bgImage={`url(${finalizedImageUrl})`}
                        bgSize="cover"
                        bgPosition="center"
                        bgRepeat="no-repeat"
                        zIndex={0}
                    />
                    <Box
                        position="fixed"
                        top={0}
                        left={0}
                        w="100vw"
                        h="100vh"
                        bg={BG_OVERLAY_OPACITY}
                        zIndex={0}
                    />
                </>
            ) : null}

            <Grid
                templateColumns={["1fr", "1fr", "1fr", `1fr 1fr ${asideWidth}`]}
                templateRows="auto 1fr"
                height={!isIframe ? "100vh" : "100%"}
                position="relative"
                zIndex={1}
            >
                {notEmbedded && (
                    <GridItem colSpan={3} rowSpan={1}>
                        <Box>
                            <Header
                                shouldHideCoverImage={!showCoverImage}
                                hasCoverImage={hasCoverImage}
                                hasMyBookingBackgroundImage={hasMyBookingBackgroundImage}
                                isModal={appMode === AppMode.Modal}
                            />
                            {renderCoverImageSection()}
                        </Box>
                    </GridItem>
                )}

                <GridItem
                    rowSpan={1}
                    colSpan={[3, 3, 3, hasAsideContent ? 2 : 3]}
                    overflowY={hasAsideContent ? "auto" : "unset"}
                >
                    <Flex justifyContent="center" h="100%">
                        <Flex
                            id="content"
                            pb={ThemeSpaceVariable.Medium}
                            w="100%"
                            maxWidth={CONTENT_MAX_WIDTH}
                            justifyContent="center"
                        >
                            <LayoutContext.Provider
                                value={{
                                    setShowCoverImage,
                                    appMode,
                                    hasMyBookingBackgroundImage,
                                    isMyBookingBackgroundImageReady,
                                    shouldShowMyBookingBackgroundImage,
                                }}
                            >
                                {children ?? <Outlet />}
                            </LayoutContext.Provider>
                        </Flex>
                    </Flex>
                </GridItem>

                <GridItem
                    rowSpan={1}
                    colSpan={1}
                    borderLeft={`1px solid ${ThemeColorVariable.OnBackgroundBorder}`}
                    display={hasAsideContent ? "block" : "none"}
                >
                    <Box id="aside-portal-container" ref={asideRef} />
                </GridItem>
            </Grid>
        </Box>
    );
}

export default Layout;
